import { supabase } from "./supabase";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

const CONNECT_TIMEOUT_MS = 15_000;
// 50 ms of audio per frame → 10 s of pre-open speech buffered at most.
const MAX_PENDING_AUDIO_FRAMES = 200;

export type VoiceTrainingEvent =
  | { kind: "open" }
  /** Client-side: an utterance was committed (sandwich), so the chat can show
   * an "Analizando…" placeholder until the transcript arrives. Not a wire event. */
  | { kind: "user_speech_committed" }
  | { kind: "user_transcript_delta"; text: string }
  | { kind: "user_transcript_final"; text: string }
  | {
      kind: "search_status";
      status: "searching" | "done" | "stale" | "error";
      message: string;
      /** Identity of the search this status belongs to. Searches run
       * concurrently, so consumers route each status by ref. */
      searchRef?: string;
    }
  | { kind: "speech_started"; itemId?: string; audioStartMs?: number }
  /** Relay verdict for a speech episode: resume the paused playback (noise
   * or a backchannel) or stop it (a real interruption with nothing left to
   * cancel server-side). */
  | { kind: "playback"; action: "resume" | "stop" }
  | { kind: "assistant_transcript_delta"; text: string; itemId?: string; responseId?: string }
  | { kind: "assistant_transcript_final"; text: string; itemId?: string; responseId?: string }
  | { kind: "assistant_audio_delta"; audio: string; itemId?: string; responseId?: string }
  | { kind: "response_cancelled"; itemId?: string; responseId?: string }
  | { kind: "tool_call"; id: string; name: string; input: Record<string, unknown> }
  | {
      kind: "tool_result";
      id: string;
      name: string;
      input: Record<string, unknown>;
      ok: boolean;
      bytes: number;
      content_preview: string;
      error?: string;
      ref?: string;
      /** Outcome of a knowledge lifecycle tool (cancel/refine). */
      knowledge_lifecycle?: Record<string, unknown>;
    }
  | {
      kind: "citation";
      id: string;
      title: string;
      preview: string;
      user_metadata?: string;
      source?: string;
    }
  | {
      kind: "fact_proposal";
      draft_id: string;
      text: string;
      status: "pending";
      detected_at: string;
      kind_name?: "fact" | "rule";
      scope?: string;
    }
  | {
      kind: "user_memory_draft";
      draft_id: string;
      operation: "create" | "update" | "delete";
      status: "pending";
      detected_at: string;
      text?: string;
      target_id?: string;
      target_text?: string;
    }
  /** The session is approaching the idle auto-stop deadline; show a
   * countdown with a keep-alive action. `secondsRemaining` is the relay's
   * estimate at emission time. */
  | { kind: "idle_warning"; secondsRemaining: number }
  /** Voice activity (or a keep-alive) reset the idle timer; hide the
   * countdown and restore the normal active state. */
  | { kind: "idle_warning_cleared" }
  /** The idle timeout elapsed; the relay is closing the session normally
   * (a clean 1000 close, not an error). */
  | { kind: "idle_timeout" }
  | { kind: "error"; code: string }
  | { kind: "close"; code: number; reason: string };

export interface VoiceTrainingInterruptPayload {
  itemId: string;
  audioEndMs: number;
  contentIndex?: number;
}

export interface VoiceTrainingClient {
  sendAudio: (pcm16: ArrayBuffer) => void;
  interruptAssistant: (payload: VoiceTrainingInterruptPayload) => void;
  /** Reset the relay's idle timer without speaking (the "keep talking"
   * action on the idle-warning countdown). */
  sendKeepalive: () => void;
  /** Close the current input buffer as one utterance (cascaded/sandwich mode
   * only). The client-side VAD calls this on end-of-speech; `e2e` relies on
   * the provider's server-side turn detection instead and never sends it. */
  sendCommit: () => void;
  /** Set the TTS playback speed for subsequent synthesized sentences
   * (cascaded/sandwich mode only). The relay clamps and applies it per turn. */
  setTtsSpeed: (speed: number) => void;
  close: () => void;
}

interface ConnectOptions {
  sessionId: string;
  onEvent: (event: VoiceTrainingEvent) => void;
}

type AssistantTranscriptSource = "audio_transcript" | "text";

interface TranscriptDedupeState {
  assistantSourceByOutputKey: Map<string, AssistantTranscriptSource>;
}

function createTranscriptDedupeState(): TranscriptDedupeState {
  return { assistantSourceByOutputKey: new Map() };
}

function toWsUrl(httpBaseUrl: string, sessionId: string, token: string): string {
  let origin = httpBaseUrl;
  if (!origin) {
    origin = `${window.location.protocol}//${window.location.host}`;
  }
  const wsOrigin = origin.replace(/^http/, "ws");
  return `${wsOrigin}/chat/sessions/${encodeURIComponent(
    sessionId,
  )}/voice-training/ws?token=${encodeURIComponent(token)}`;
}

function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseJsonObject(value: unknown): Record<string, unknown> {
  if (isRecord(value)) return value;
  if (typeof value !== "string") return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function stringField(payload: Record<string, unknown>, field: string): string {
  const value = payload[field];
  return typeof value === "string" ? value : "";
}

function optionalStringField(payload: Record<string, unknown>, field: string): string | undefined {
  const value = payload[field];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function numberField(payload: Record<string, unknown>, field: string): number {
  const value = payload[field];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function optionalNumberField(payload: Record<string, unknown>, field: string): number | undefined {
  const value = payload[field];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function assistantItemId(payload: Record<string, unknown>): string | undefined {
  return optionalStringField(payload, "item_id");
}

function assistantResponseId(payload: Record<string, unknown>): string | undefined {
  return optionalStringField(payload, "response_id");
}

function assistantOutputKey(payload: Record<string, unknown>): string | undefined {
  const responseId = assistantResponseId(payload);
  const itemId = assistantItemId(payload);
  if (responseId === undefined && itemId === undefined) return undefined;
  return `${responseId ?? ""}:${itemId ?? ""}`;
}

function shouldEmitAssistantTranscriptDelta(
  payload: Record<string, unknown>,
  source: AssistantTranscriptSource,
  delta: string,
  state?: TranscriptDedupeState,
): boolean {
  if (state === undefined || delta.length === 0) return true;
  const key = assistantOutputKey(payload);
  if (key === undefined) return true;

  const selectedSource = state.assistantSourceByOutputKey.get(key);
  if (selectedSource === undefined) {
    state.assistantSourceByOutputKey.set(key, source);
    return true;
  }
  return selectedSource === source;
}

function emitResponseDoneTools(
  payload: Record<string, unknown>,
  emit: (event: VoiceTrainingEvent) => void,
): void {
  const response = payload.response;
  if (!isRecord(response) || !Array.isArray(response.output)) return;
  for (const item of response.output) {
    if (!isRecord(item) || item.type !== "function_call") continue;
    const id = typeof item.call_id === "string" ? item.call_id : "";
    const name = typeof item.name === "string" ? item.name : "";
    if (!id || !name) continue;
    emit({
      kind: "tool_call",
      id,
      name,
      input: parseJsonObject(item.arguments),
    });
  }
}

function emitRealtimeEvent(
  payload: unknown,
  emit: (event: VoiceTrainingEvent) => void,
  state?: TranscriptDedupeState,
): void {
  if (!isRecord(payload)) return;
  const type = typeof payload.type === "string" ? payload.type : "";
  const delta = typeof payload.delta === "string" ? payload.delta : "";
  const transcript = typeof payload.transcript === "string" ? payload.transcript : "";
  const text = typeof payload.text === "string" ? payload.text : "";
  switch (type) {
    case "input_audio_buffer.speech_started": {
      const itemId = optionalStringField(payload, "item_id");
      const audioStartMs = optionalNumberField(payload, "audio_start_ms");
      const event: VoiceTrainingEvent = { kind: "speech_started" };
      if (itemId !== undefined) event.itemId = itemId;
      if (audioStartMs !== undefined) event.audioStartMs = audioStartMs;
      emit(event);
      return;
    }
    case "conversation.item.input_audio_transcription.delta":
      emit({ kind: "user_transcript_delta", text: delta });
      return;
    case "conversation.item.input_audio_transcription.completed":
      emit({ kind: "user_transcript_final", text: transcript || text });
      return;
    case "beflow.voice_playback": {
      const action = stringField(payload, "action");
      if (action !== "resume" && action !== "stop") return;
      emit({ kind: "playback", action });
      return;
    }
    case "beflow.voice_search_status": {
      const status = stringField(payload, "status");
      if (status !== "searching" && status !== "done" && status !== "stale" && status !== "error") {
        return;
      }
      const searchRef = optionalStringField(payload, "search_ref");
      emit({
        kind: "search_status",
        status,
        message: stringField(payload, "message"),
        ...(searchRef !== undefined ? { searchRef } : {}),
      });
      return;
    }
    case "response.output_audio_transcript.delta":
    case "response.output_text.delta": {
      const source: AssistantTranscriptSource =
        type === "response.output_audio_transcript.delta" ? "audio_transcript" : "text";
      if (!shouldEmitAssistantTranscriptDelta(payload, source, delta, state)) return;
      const itemId = assistantItemId(payload);
      const responseId = assistantResponseId(payload);
      const event: VoiceTrainingEvent = { kind: "assistant_transcript_delta", text: delta };
      if (itemId !== undefined) event.itemId = itemId;
      if (responseId !== undefined) event.responseId = responseId;
      emit(event);
      return;
    }
    case "response.output_audio_transcript.done":
    case "response.output_text.done": {
      const itemId = assistantItemId(payload);
      const responseId = assistantResponseId(payload);
      const event: VoiceTrainingEvent = {
        kind: "assistant_transcript_final",
        text: transcript || text,
      };
      if (itemId !== undefined) event.itemId = itemId;
      if (responseId !== undefined) event.responseId = responseId;
      emit(event);
      return;
    }
    case "response.output_audio.delta":
    case "response.audio.delta": {
      const itemId = assistantItemId(payload);
      const responseId = assistantResponseId(payload);
      const event: VoiceTrainingEvent = { kind: "assistant_audio_delta", audio: delta };
      if (itemId !== undefined) event.itemId = itemId;
      if (responseId !== undefined) event.responseId = responseId;
      emit(event);
      return;
    }
    case "response.cancelled": {
      const itemId = assistantItemId(payload);
      const responseId = assistantResponseId(payload);
      const event: VoiceTrainingEvent = { kind: "response_cancelled" };
      if (itemId !== undefined) event.itemId = itemId;
      if (responseId !== undefined) event.responseId = responseId;
      emit(event);
      return;
    }
    case "response.done": {
      const response = payload.response;
      if (isRecord(response) && response.status === "cancelled") {
        emit({
          kind: "response_cancelled",
          ...(typeof response.id === "string" ? { responseId: response.id } : {}),
        });
      }
      emitResponseDoneTools(payload, emit);
      return;
    }
    case "beflow.tool_result": {
      const id = stringField(payload, "id");
      const name = stringField(payload, "name");
      if (!id || !name) return;
      const ok = payload.ok === true;
      const error = optionalStringField(payload, "error");
      const ref = optionalStringField(payload, "ref");
      const lifecycle = payload.knowledge_lifecycle;
      emit({
        kind: "tool_result",
        id,
        name,
        input: parseJsonObject(payload.input),
        ok,
        bytes: numberField(payload, "bytes"),
        content_preview: stringField(payload, "content_preview"),
        ...(error ? { error } : {}),
        ...(ref ? { ref } : {}),
        ...(isRecord(lifecycle) ? { knowledge_lifecycle: lifecycle } : {}),
      });
      return;
    }
    case "beflow.citation": {
      const id = stringField(payload, "id");
      const title = stringField(payload, "title");
      if (!id || !title) return;
      const userMetadata = optionalStringField(payload, "user_metadata");
      const source = optionalStringField(payload, "source");
      emit({
        kind: "citation",
        id,
        title,
        preview: stringField(payload, "preview"),
        ...(userMetadata ? { user_metadata: userMetadata } : {}),
        ...(source ? { source } : {}),
      });
      return;
    }
    case "beflow.fact_proposal": {
      const draftId = stringField(payload, "draft_id");
      const proposalText = stringField(payload, "text");
      if (!draftId || !proposalText) return;
      const kindName =
        payload.kind === "rule" ? "rule" : payload.kind === "fact" ? "fact" : undefined;
      const scope = optionalStringField(payload, "scope");
      emit({
        kind: "fact_proposal",
        draft_id: draftId,
        text: proposalText,
        status: "pending",
        detected_at: stringField(payload, "detected_at") || new Date().toISOString(),
        ...(kindName ? { kind_name: kindName } : {}),
        ...(scope ? { scope } : {}),
      });
      return;
    }
    case "beflow.user_memory_draft": {
      const draftId = stringField(payload, "draft_id");
      const operationRaw = payload.operation;
      const operation =
        operationRaw === "create" || operationRaw === "update" || operationRaw === "delete"
          ? operationRaw
          : undefined;
      if (!draftId || !operation) return;
      const memoryText = optionalStringField(payload, "text");
      const targetId = optionalStringField(payload, "target_id");
      const targetText = optionalStringField(payload, "target_text");
      emit({
        kind: "user_memory_draft",
        draft_id: draftId,
        operation,
        status: "pending",
        detected_at: stringField(payload, "detected_at") || new Date().toISOString(),
        ...(memoryText ? { text: memoryText } : {}),
        ...(targetId ? { target_id: targetId } : {}),
        ...(targetText ? { target_text: targetText } : {}),
      });
      return;
    }
    case "beflow.idle_warning":
      emit({ kind: "idle_warning", secondsRemaining: numberField(payload, "seconds_remaining") });
      return;
    case "beflow.idle_warning_cleared":
      emit({ kind: "idle_warning_cleared" });
      return;
    case "beflow.idle_timeout":
      emit({ kind: "idle_timeout" });
      return;
    case "error":
      emit({ kind: "error", code: text || "upstream_error" });
      return;
    default:
      return;
  }
}

export async function connectVoiceTraining(options: ConnectOptions): Promise<VoiceTrainingClient> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new Error("not_authenticated");
  }
  const ws = new WebSocket(toWsUrl(apiBaseUrl, options.sessionId, token));
  const transcriptDedupeState = createTranscriptDedupeState();
  // Closing a still-CONNECTING socket surfaces as an abnormal close (1006),
  // and teardown races can close with other non-1000 codes. None of that is
  // an error when the user initiated the stop.
  let closedByClient = false;
  // Audio captured while the socket is still connecting. The mic starts
  // before the handshake completes, so dropping these frames eats the
  // beginning of the user's first utterance; buffer and flush on open.
  const pendingAudio: string[] = [];

  const connectTimer = setTimeout(() => {
    if (ws.readyState !== WebSocket.OPEN) {
      // Surfaces as `error` + `close` via the close listener below.
      ws.close(4000, "connection_timeout");
    }
  }, CONNECT_TIMEOUT_MS);

  ws.addEventListener("open", () => {
    clearTimeout(connectTimer);
    for (const audio of pendingAudio) {
      ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio }));
    }
    pendingAudio.length = 0;
    options.onEvent({ kind: "open" });
  });

  ws.addEventListener("message", (event) => {
    if (typeof event.data !== "string") return;
    let payload: unknown;
    try {
      payload = JSON.parse(event.data);
    } catch {
      return;
    }
    emitRealtimeEvent(payload, options.onEvent, transcriptDedupeState);
  });

  ws.addEventListener("close", (event) => {
    clearTimeout(connectTimer);
    if (!closedByClient && event.code !== 1000) {
      options.onEvent({ kind: "error", code: event.reason || `close_${event.code}` });
    }
    options.onEvent({ kind: "close", code: event.code, reason: event.reason });
  });

  return {
    sendAudio: (pcm16: ArrayBuffer) => {
      if (ws.readyState === WebSocket.CONNECTING) {
        if (pendingAudio.length < MAX_PENDING_AUDIO_FRAMES) {
          pendingAudio.push(bufferToBase64(pcm16));
        }
        return;
      }
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        JSON.stringify({
          type: "input_audio_buffer.append",
          audio: bufferToBase64(pcm16),
        }),
      );
    },
    interruptAssistant: ({ itemId, audioEndMs, contentIndex = 0 }) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "response.cancel" }));
      ws.send(
        JSON.stringify({
          type: "conversation.item.truncate",
          item_id: itemId,
          content_index: contentIndex,
          audio_end_ms: audioEndMs,
        }),
      );
    },
    sendKeepalive: () => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "beflow.voice_keepalive" }));
    },
    sendCommit: () => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
    },
    setTtsSpeed: (speed: number) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "beflow.set_tts_speed", speed }));
    },
    close: () => {
      closedByClient = true;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, "client_closed");
      }
    },
  };
}

export const __test_internals = { toWsUrl, bufferToBase64, emitRealtimeEvent };
